import { createContext, useEffect, useState } from 'react';

import { client, getAuthToken, removeAuthToken, setAuthToken } from '@/shared/lib/client';
import { useUserStore } from '@/store/useUserStore';
import { UserInfo } from '@/types/user';
import { generateInternalSSOUrl, isInternalDomain } from '@/utils/const';

export const UserContext = createContext<{
  user: UserInfo | null;
  setUser: (user: UserInfo | null) => void;
  hasToken: boolean;
  isLoading: boolean;
}>({
  user: null,
  setUser: () => {},
  hasToken: false,
  isLoading: false,
});

// 检查是否在 iframe 中
const isInIframe = () => {
  try {
    return window.self !== window.top;
  } catch {
    return true;
  }
};

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<UserInfo | null>(null);
  const [hasToken, setHasToken] = useState(false);
  const setUserToStore = useUserStore((state) => state.setUser);

  // 根据域名判断初始 loading 状态
  // 内部域名：总是 loading（需要调用 /api/auth/oauth/user 验证 SSO）
  // 外部域名：有 token 时才 loading
  const [isLoading, setIsLoading] = useState(() => {
    if (isInternalDomain()) {
      return true; // 内部域名总是需要验证
    }
    return !!getAuthToken(); // 外部域名检查 token
  });

  useEffect(() => {
    const isInternal = isInternalDomain();
    const urlParams = new URLSearchParams(window.location.search);

    // iframe 环境特殊处理
    if (isInIframe()) {
      if (isInternal) {
        // 内网情况：query 带了 username，手动设置用户
        const username = urlParams.get('username');
        if (username) {
          const mockUser: UserInfo = {
            userId: username,
            username: username,
            displayName: username,
            name: username,
            avatar: '',
          };
          setUser(mockUser);
          setUserToStore(mockUser);
          setHasToken(true);
          setIsLoading(false);
          return;
        }
      } else {
        // 外网情况：query 带了 token，手动设置 cookie
        const token = urlParams.get('token');
        if (token) {
          setAuthToken(token);
          setHasToken(true);
          setIsLoading(false);
          return;
        }
      }
    }

    if (isInternal) {
      // 内部域名：直接调用 /api/auth/oauth/user，依赖 SSO cookie
      setIsLoading(true);
      setHasToken(true); // 假设内部域名有 SSO 认证

      client
        .get('/api/auth/sso/user')
        .then((res) => {
          setUser(res.data);
          setUserToStore(res.data); // 同步到 store
        })
        .catch((error) => {
          console.error('Failed to fetch user info (Internal SSO):', error);
          setHasToken(false);
          setUser(null);
          // 内部域名认证失败，跳转到 SSO 登录页
          // 使用当前路径作为重定向目标，这样登录后会回到原来的页面
          const currentPath = window.location.pathname + window.location.search;
          window.location.href = generateInternalSSOUrl(currentPath);
        })
        .finally(() => {
          setIsLoading(false);
        });
    } else {
      // 外部域名：检查 ide_auth_token
      const token = getAuthToken();
      setHasToken(!!token);

      if (!token) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      client
        .get('/api/auth/oauth/user')
        .then((res) => {
          setUser(res.data);
          setUserToStore(res.data); // 同步到 store
        })
        .catch((error) => {
          console.error('Failed to fetch user info:', error);
          // 如果获取用户信息失败，清除 token 和相关状态
          setHasToken(false);
          setUser(null);
          // 可选：清除无效的 token cookie
          removeAuthToken();
        })
        .finally(() => {
          setIsLoading(false);
        });
    }
  }, []);

  return (
    <UserContext.Provider value={{ user, setUser, hasToken, isLoading }}>
      {children}
    </UserContext.Provider>
  );
};
